import {
  fmtUsdDisplay,
  pctDisplayNumber,
} from './termSheetFormatters';

function isFilledNumber(value) {
  if (value === '' || value == null) {
    return false;
  }
  return Number.isFinite(Number(value));
}

export function hasRecentCohortCap(fields) {
  return (
    isFilledNumber(fields.maxPctIncreaseFromLast) ||
    isFilledNumber(fields.maxAmountIncreaseFromLast)
  );
}

function pctOrPlaceholder(value) {
  const pct = pctDisplayNumber(value);
  return pct == null ? '[]' : pct;
}

function usdOrPlaceholder(value) {
  const formatted = fmtUsdDisplay(value);
  return formatted == null ? '[]' : formatted.replace(/^\$/, '');
}

function buildRecentCohortCapLimit(fields, getPct, getUsd) {
  const hasPct = isFilledNumber(fields.maxPctIncreaseFromLast);
  const hasAmt = isFilledNumber(fields.maxAmountIncreaseFromLast);

  if (hasPct && hasAmt) {
    return `more than ${getPct(fields.maxPctIncreaseFromLast)}% OR US $${getUsd(
      fields.maxAmountIncreaseFromLast
    )}`;
  }
  if (hasPct) {
    return `more than ${getPct(fields.maxPctIncreaseFromLast)}%`;
  }
  if (hasAmt) {
    return `US $${getUsd(fields.maxAmountIncreaseFromLast)}`;
  }
  return '';
}

export function buildPctOfFundingClause(fields) {
  const pct = pctOrPlaceholder(fields.pctOfFunding);
  const base = `Up to ${pct}% of the associated cohort marketing spend per cohort`;
  if (hasRecentCohortCap(fields)) {
    return base;
  }
  return `${base}, subject to the Company’s monthly marketing plan`;
}

export function buildMonthlyCohortLine(fields, getNum, getUsd, totalCohortInvestment = null) {
  const cohorts = getNum(fields.monthlyCohorts);
  const perCohort = getUsd(fields.maxAmountPerCohort);
  const total =
    totalCohortInvestment != null ? getUsd(totalCohortInvestment) : '[[]]';
  return `${cohorts} monthly cohort investments of up to US $${perCohort} each (a total cohort investment amount of up to US $${total})`;
}

export function buildCohortCapLine(fields, getPct, getUsd) {
  if (!hasRecentCohortCap(fields)) {
    return '';
  }
  const limit = buildRecentCohortCapLimit(fields, getPct, getUsd);
  return `An amount that is not higher than the most recent cohort investment by ${limit}`;
}

export function buildMinInvestmentLine(fields, getUsd) {
  if (!hasMinInvestment(fields)) {
    return '';
  }
  return `In each case, subject to a minimum monthly investment of US $${getUsd(
    fields.minInvestment
  )}`;
}

export function buildCohortCapClause(fields) {
  const line = buildCohortCapLine(fields, pctOrPlaceholder, usdOrPlaceholder);
  if (!line) {
    return '';
  }
  return ` ${line.charAt(0).toLowerCase()}${line.slice(1)}`;
}

export function buildMarketingSpendSection(fields) {
  return `${buildPctOfFundingClause(fields)}${buildCohortCapClause(fields)}`;
}

export function buildPctOfFundingDocxToken(value) {
  const pct = pctDisplayNumber(value);
  return pct == null ? '[]' : pct;
}

export function buildCohortCapClauseDocx(fields, fmtUsdDocx) {
  return buildCohortCapLine(fields, buildPctOfFundingDocxToken, fmtUsdDocx);
}

export function buildMonthlyCohortLineDocx(fields, fmtUsdDocx, fmtNumDocx, totalCohortInvestment) {
  return buildMonthlyCohortLine(
    fields,
    fmtNumDocx,
    fmtUsdDocx,
    totalCohortInvestment
  );
}

export function buildMinInvestmentLineDocx(fields, fmtUsdDocx) {
  return buildMinInvestmentLine(fields, fmtUsdDocx);
}

export function hasMinInvestment(fields) {
  return isFilledNumber(fields.minInvestment);
}

export function buildMinInvestmentClause(fields, getUsd) {
  if (!hasMinInvestment(fields)) {
    return '';
  }
  return `[In each case, subject to a minimum monthly investment of US $${getUsd(
    fields.minInvestment
  )}]`;
}

export function buildMinInvestmentClauseDocx(fields, fmtUsdDocx) {
  return buildMinInvestmentClause(fields, fmtUsdDocx);
}

export function hasMaxCac(fields) {
  return isFilledNumber(fields.maxCac);
}

export function buildMaxCacLimitPhrase(fields, getUsd) {
  if (hasMaxCac(fields)) {
    return `no more than US $${getUsd(fields.maxCac)}`;
  }
  return 'no more than an amount to be determined in the definitive agreement';
}

export function buildMaxCacClause(fields, getUsd) {
  return `Each investment will be subject, among other things, to a customer acquisition cost for the applicable cohort being ${buildMaxCacLimitPhrase(
    fields,
    getUsd
  )}`;
}

export function buildMaxCacClauseDocx(fields, fmtUsdDocx) {
  return buildMaxCacLimitPhrase(fields, fmtUsdDocx);
}
